import { Suspense, use } from "react";
import { TM } from "@litres-libs/tracker-manager";

import { getConfig } from "lib/get-config";
import { useTranslation } from "lib/i18n/use-translation";
import { ContentLayoutContext } from "lib/layout/use-content-layout";
import { NavigationContext } from "lib/navigation/use-navigation";
import { getPersonName } from "lib/toc/person-name";
import { useToc } from "lib/toc/use-toc";
import { appendTrackingParams } from "lib/tracking/params";
import { getBuyUrl } from "lib/urls/buy";

import LogoIcon from "assets/icons/logo.svg?react";
import css from "./footer.module.scss";
import { Scrubber } from "./scrubber";

function Logo() {
  return (
    <a
      className={css.logo}
      href={appendTrackingParams(import.meta.env.VITE_LITRES_URL)}
      aria-label="Litres"
    >
      <LogoIcon width={113} height={18} />
    </a>
  );
}

function SaleBlock() {
  const { t } = useTranslation();
  const { toc } = useToc();
  const { texttrialbutton, showSaleBlock } = getConfig();
  const artId = toc?.Meta.ArtID;

  const author = toc?.Meta.Authors?.find(a => a.Role === "author");
  const fullName = author ? getPersonName(author) : t("Неизвестный автор");

  const bookTitle = toc?.Meta.Title || t("Без названия");

  const handleBuyClick = () => {
    if (showSaleBlock) {
      TM.sendEventSync({
        eventType: "fragment_click_buy",
        params: {
          id: artId,
          source_internal: "book_card",
        },
      });
    }

    window.location.href = getBuyUrl();
  };

  return (
    <div className={css.saleBlock}>
      <div className={css.saleBookInfo}>
        <span className={css.saleTitle}>{bookTitle}</span>
        <span className={css.saleAuthor}>{fullName}</span>
      </div>
      <a className={css.saleBuyButton} onClick={handleBuyClick}>
        {texttrialbutton || t("Купить")}
      </a>
    </div>
  );
}

export function Footer() {
  const { t } = useTranslation();
  const { trials } = getConfig();
  const isFragment = trials === 1;

  return (
    <div className={css.root}>
      {isFragment && <SaleBlock />}
      <Scrubber />
      <div className={css.bottom}>
        <Logo />
        <Suspense fallback={<div>{t("Загрузка...")}</div>}>
          <TotalPages />
        </Suspense>
      </div>
    </div>
  );
}

function TotalPages() {
  const { page, isLayoutComplete } = use(NavigationContext)!;
  const { totalPages, measurementProgress } = use(ContentLayoutContext)!;
  const { t } = useTranslation();

  if (!isLayoutComplete) {
    const text = t("Подсчет страниц...");
    const charCount = text.length;

    const rawProgress =
      measurementProgress && measurementProgress.total > 0
        ? measurementProgress.measured / measurementProgress.total
        : 0;

    const charsToFill = Math.floor(rawProgress * charCount);

    return (
      <div className={css.progressText}>
        {text.split("").map((char, i) => (
          <span
            key={i}
            className={i < charsToFill ? css.filledChar : css.unfilledChar}
          >
            {char}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className={css.totalPages}>
      {t("Страница {{page}} из {{total}}", {
        page: page + 1,
        total: totalPages,
      })}
    </div>
  );
}
